/**
 * Modo Comparar: K e Q lado a lado, cada painel com viewport de zoom próprio // ux-neutrality:allow doc-comment
 * (E36.F2). Sobre a contestada empilham as camadas: marcações do sistema // ux-neutrality:allow doc-comment
 * (E23.F10/E36.F1), régua (ADR-0039 §D) e anotações manuais (E36.F3). // ux-neutrality:allow doc-comment
 *
 * E37.F1: o estado de zoom/pan dos dois painéis vive AQUI (viewports // ux-neutrality:allow doc-comment
 * controlados). Com `syncPanels` ligado (default), qualquer gesto espelha // ux-neutrality:allow doc-comment
 * zoom e pan no outro painel — examinar a mesma região no mesmo nível de // ux-neutrality:allow doc-comment
 * zoom é o gesto central da perícia. Ligar o sync alinha K ao estado do Q // ux-neutrality:allow doc-comment
 * (a contestada é o objeto de estudo). A barra de espaço ativa pan // ux-neutrality:allow doc-comment
 * temporário nos dois painéis com prioridade sobre régua/desenho. Tudo // ux-neutrality:allow doc-comment
 * efêmero e apresentação-only (ADR-0039 §A). // ux-neutrality:allow doc-comment
 *
 * Módulo extraído do ContestedCanvasTools por max-lines (mesma lição do // ux-neutrality:allow doc-comment
 * próprio ContestedCanvasTools/InstrumentPanels). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import type { CriterionBandStatus, QuestionedLocalizationsData } from '@/lib/case_workspace';
import { MeasurableImage } from './ContestedInspectionFilters';
import { ContestedMarkerOverlay } from './ContestedMarkerOverlay';
import { useSpacePan } from './useSpacePan';
import {
  ZOOM_PAN_IDENTITY,
  ZoomPanViewport,
  type ZoomCommand,
  type ZoomPanState,
} from './ZoomPanViewport';

function SignaturePlaceholder(): JSX.Element {
  // E37.F3.T02: preenche a altura de trabalho real (max-h-full sobrepõe o
  // max-h-[26rem] da .signature-img — placeholder fica fora do zoompan-viewport).
  return (
    <div
      className="signature-img h-full max-h-full min-h-[20rem] animate-pulse bg-fg/[0.06]"
      data-testid="signature-placeholder"
      aria-hidden="true"
    />
  );
}

/**
 * Prancha de espécime da 2B (E54.F5.T08): a área de trabalho com a legenda mono
 * no rodapé, como a figura de um laudo.
 *
 * A legenda fica FORA do `.contested-marked`: as camadas de marcação, régua e
 * anotação cobrem aquele box por `inset-0`, e um rodapé lá dentro passaria a
 * fazer parte da área coberta — os marcadores deixariam de casar com o traço.
 *
 * Sem barra de escala, ao contrário da prancha: o front não recebe o DPI com que
 * o espécime foi digitalizado, e desenhar "10 mm" sem saber a escala seria uma
 * medida falsa numa peça que vai para os autos. Divergência deliberada,
 * registrada em docs/design/comparacoes/README.md.
 */
function SpecimenPlate({
  caption,
  testId,
  children,
}: {
  caption: string;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <figure className="specimen-plate" data-testid={testId}>
      <div className="specimen-plate-body">{children}</div>
      <figcaption className="specimen-plate-caption">{caption}</figcaption>
    </figure>
  );
}

export interface CompareViewProps {
  patternSrc: string | null;
  contestedSrc: string | null;
  showMarkers: boolean;
  localizations: QuestionedLocalizationsData | null;
  criteriaStatus: Record<string, CriterionBandStatus> | null;
  rulerActive: boolean;
  onRulerMeasured?: (() => void) | undefined;
  zoomCommand: ZoomCommand | null;
  /** E37.F1.T03: espelhar zoom/pan entre K e Q (toggle na toolbar). */
  syncPanels: boolean;
  /** E36.F3: camada de anotações manuais sobre a contestada (montada pelo pai). */
  annotationsLayer?: React.ReactNode;
  /** E36.F3: desenhar desativa o pan por arrasto no painel da contestada
   * (um modo ativo por vez — o arrasto esquerdo é do desenho). */
  contestedPanDisabled?: boolean;
}

export function CompareView({
  patternSrc,
  contestedSrc,
  showMarkers,
  localizations,
  criteriaStatus,
  rulerActive,
  onRulerMeasured,
  zoomCommand,
  syncPanels,
  annotationsLayer = null,
  contestedPanDisabled = false,
}: CompareViewProps): JSX.Element {
  // E37.F1: espaço = pan temporário nos dois painéis (prioridade sobre as
  // ferramentas enquanto pressionado); estado de zoom/pan por painel vive aqui.
  const spacePan = useSpacePan();
  const [patternState, setPatternState] = useState<ZoomPanState>(ZOOM_PAN_IDENTITY);
  const [contestedState, setContestedState] = useState<ZoomPanState>(ZOOM_PAN_IDENTITY);

  // Ligar o sync alinha K ao estado corrente do Q (objeto de estudo); com o
  // sync já ligado o espelhamento acontece nos handlers de gesto.
  const prevSyncRef = useRef(syncPanels);
  useEffect(() => {
    if (syncPanels && !prevSyncRef.current) setPatternState(contestedState);
    prevSyncRef.current = syncPanels;
  }, [syncPanels, contestedState]);

  function onPatternChange(next: ZoomPanState): void {
    setPatternState(next);
    if (syncPanels) setContestedState(next);
  }

  function onContestedChange(next: ZoomPanState): void {
    setContestedState(next);
    if (syncPanels) setPatternState(next);
  }

  return (
    <div className="side-by-side">
      <SpecimenPlate
        caption={ptBR.workspaceCaso.plateCaptionPattern}
        testId="specimen-plate-pattern"
      >
        {patternSrc ? (
          <ZoomPanViewport
            testId="zoom-viewport-pattern"
            panDraggable={!rulerActive}
            command={zoomCommand}
            state={patternState}
            onStateChange={onPatternChange}
            spacePan={spacePan}
          >
            <MeasurableImage
              src={patternSrc}
              alt="padrão selecionado"
              className="signature-img"
              rulerActive={rulerActive}
              onRulerMeasured={onRulerMeasured}
            />
          </ZoomPanViewport>
        ) : (
          <SignaturePlaceholder />
        )}
      </SpecimenPlate>
      <SpecimenPlate
        caption={ptBR.workspaceCaso.plateCaptionContested}
        testId="specimen-plate-contested"
      >
        {contestedSrc ? (
          <ZoomPanViewport
            testId="zoom-viewport-contested"
            panDraggable={!rulerActive && !contestedPanDisabled}
            command={zoomCommand}
            state={contestedState}
            onStateChange={onContestedChange}
            spacePan={spacePan}
          >
            <div className="contested-marked">
              <MeasurableImage
                src={contestedSrc}
                alt={ptBR.workspaceCaso.contestedTitle}
                className="signature-img"
                rulerActive={rulerActive}
                onRulerMeasured={onRulerMeasured}
              />
              {showMarkers && localizations && (
                <ContestedMarkerOverlay
                  localizations={localizations}
                  criteriaStatus={criteriaStatus}
                />
              )}
              {annotationsLayer}
            </div>
          </ZoomPanViewport>
        ) : (
          <SignaturePlaceholder />
        )}
      </SpecimenPlate>
    </div>
  );
}
